import * as React from 'react';

interface PaginationProps {
  current_page: number;
  total_pages: number;
  records: number;
  prev_page: number;
  next_page: number;
  navigateTo: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  current_page,
  total_pages,
  records,
  prev_page,
  next_page,
  navigateTo
}) => {
  // Calculate which pages to show
  const getVisiblePages = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;

    if (total_pages <= maxVisible) {
      // Show all pages if we have few enough
      for (let i = 1; i <= total_pages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end for middle section
      let start = Math.max(2, current_page - 1);
      let end = Math.min(total_pages - 1, current_page + 1);

      // If we're near the beginning, show more pages at start
      if (current_page <= 3) {
        end = Math.min(total_pages - 1, 5);
      }

      // If we're near the end, show more pages at end
      if (current_page >= total_pages - 2) {
        start = Math.max(2, total_pages - 4);
      }

      // Add ellipsis if there's a gap after first page
      if (start > 2) {
        pages.push('...');
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis if there's a gap before last page
      if (end < total_pages - 1) {
        pages.push('...');
      }

      // Always show last page (if not already included)
      if (total_pages > 1) {
        pages.push(total_pages);
      }
    }

    return pages;
  };

  const visiblePages = getVisiblePages();

  // Modern button styles
  const baseButtonStyle: React.CSSProperties = {
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    fontWeight: 600,
    fontSize: '0.875rem',
    border: '1px solid #e8ecef',
    background: 'white',
    color: '#034ba6',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const activeButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    background: 'linear-gradient(135deg, #085ec8, #176fc9)',
    color: 'white',
    border: '1px solid #085ec8',
    boxShadow: '0 4px 12px rgba(8, 94, 200, 0.3)',
  };

  const disabledButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    opacity: 0.5,
    cursor: 'not-allowed',
    background: '#f8f9fa',
    color: '#6c757d',
  };

  return (
    <div className="col-12 mt-4">
      <div className="row">
        <div className="col-12 text-center">
          <div style={{
            background: 'white',
            border: '1px solid #e8ecef',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
          }}>
            <nav aria-label="Job search pagination">
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}>
                {/* Previous Button */}
                <button
                  style={current_page === 1 ? disabledButtonStyle : baseButtonStyle}
                  onClick={() => navigateTo(prev_page)}
                  disabled={current_page === 1}
                  aria-label="Previous page"
                  onMouseOver={(e) => {
                    if (current_page !== 1) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(8, 94, 200, 0.2)';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <i className="fas fa-chevron-left"></i> Previous
                </button>

                {/* Page Numbers */}
                {visiblePages.map((page, index) => {
                  if (page === '...') {
                    return (
                      <span
                        key={`ellipsis-${index}`}
                        style={{
                          padding: '0.5rem',
                          color: '#6c757d',
                          fontWeight: 600,
                        }}
                      >
                        ...
                      </span>
                    );
                  } else {
                    const pageNum = page as number;
                    const isActive = current_page === pageNum;
                    return (
                      <button
                        key={pageNum}
                        style={isActive ? activeButtonStyle : baseButtonStyle}
                        onClick={() => navigateTo(pageNum)}
                        aria-label={`Go to page ${pageNum}`}
                        aria-current={isActive ? 'page' : undefined}
                        onMouseOver={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(8, 94, 200, 0.2)';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                })}

                {/* Next Button */}
                <button
                  style={current_page === total_pages ? disabledButtonStyle : baseButtonStyle}
                  onClick={() => navigateTo(next_page)}
                  disabled={current_page === total_pages}
                  aria-label="Next page"
                  onMouseOver={(e) => {
                    if (current_page !== total_pages) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(8, 94, 200, 0.2)';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  Next <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </nav>

            {/* Results Summary */}
            <div style={{
              marginTop: '1.5rem',
              paddingTop: '1rem',
              borderTop: '1px solid #e8ecef',
            }}>
              <div style={{
                color: '#6c757d',
                fontSize: '0.875rem',
                fontWeight: 500,
              }}>
                Page {current_page} of {total_pages}
              </div>
            </div>

            {/* Jump to Page (for large result sets) */}
            {total_pages > 10 && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}>
                  <label
                    htmlFor="jump-to-page"
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#034ba6',
                      padding: '0.5rem 0.75rem',
                      background: '#f8f9fa',
                      border: '1px solid #e8ecef',
                      borderRadius: '0.5rem 0 0 0.5rem',
                    }}
                  >
                    Jump to page:
                  </label>
                  <input
                    id="jump-to-page"
                    type="number"
                    style={{
                      width: '80px',
                      padding: '0.5rem',
                      border: '1px solid #e8ecef',
                      borderRadius: '0 0.5rem 0.5rem 0',
                      fontSize: '0.875rem',
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                    }}
                    min="1"
                    max={total_pages}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const target = e.target as HTMLInputElement;
                        const page = parseInt(target.value);
                        if (page >= 1 && page <= total_pages) {
                          navigateTo(page);
                          target.value = '';
                        }
                      }
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#085ec8';
                      e.currentTarget.style.outline = 'none';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(8, 94, 200, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e8ecef';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    placeholder={current_page.toString()}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
